<script lang="ts" setup>
import { Co2QuizQuestion } from '@/models/co2-quiz.model';
import { PropType, ref } from 'vue';
import { useStore } from 'vuex';
import { ShiftCMSState } from '@/store';
import {
  Co2QuizStoreActions,
  Co2QuizStoreState,
} from '@/stores/co2-quiz.store';
import SortItems from '@/components/common/SortItems.vue';

const props = defineProps({
  data: {
    type: Array as PropType<Co2QuizQuestion[]>,
    required: true,
  },
});

const store = useStore<ShiftCMSState & { co2: Co2QuizStoreState }>();

const drag = ref<boolean>(false);
const dragItem = ref<Co2QuizQuestion | null>(null);

const dragStart = (e: Event, el: Co2QuizQuestion) => {
  drag.value = true;
  dragItem.value = el;

  return null;
};

const dragEnd = () => {
  drag.value = false;
  return null;
};

const dragDrop = async (evt: DragEvent, el: Co2QuizQuestion | null) => {
  evt.preventDefault();
  drag.value = false;

  await store.dispatch(Co2QuizStoreActions.UpdateCo2QuizQuestion, {
    ...dragItem.value,
    parentQuestion: el,
  });

  await store.dispatch(Co2QuizStoreActions.GetCo2Questions);

  dragItem.value = null;
};

const dragOver = (evt: DragEvent) => {
  evt.preventDefault();

  if (evt.target) {
    (evt.target as Element).classList.add('drag-active');
  }
};
const dragLeave = (evt: DragEvent) => {
  evt.preventDefault();
  if (evt.target) {
    (evt.target as Element).classList.remove('drag-active');
  }
};

const save = (v: number[]) => {
  store.dispatch(Co2QuizStoreActions.UpdateQuestionOrder, v);
};
</script>
<template>
  <v-container>
    <h2>Edit co2 question hierarchy</h2>
    <v-alert type="info">
      Drag the co2 question onto their parent. This is allowed only for
      <i>one level</i>.
    </v-alert>

    <v-card class="mt-4 pb-4">
      <ul class="mt-8 pl-8 draglist">
        <li
          v-for="cat in data
            .filter((c) => !c.parentQuestion)
            .sort((a, b) => a.index - b.index)"
          :key="cat.id"
        >
          <div class="item">
            <div
              class="name"
              draggable="true"
              @dragstart="(e) => dragStart(e, cat)"
              @dragend="dragEnd"
            >
              {{ cat.title }}
            </div>
            <div
              v-if="drag && dragItem?.id !== cat.id"
              class="drop-target"
              @drop="(e) => dragDrop(e, cat)"
              @dragover="(e) => dragOver(e)"
              @dragleave="(e) => dragLeave(e)"
            ></div>
          </div>
          <ul class="pl-8">
            <li
              v-for="cat2 in data
                .filter((c) => c.parentQuestion?.id === cat.id)
                .sort((a, b) => a.index - b.index)"
              :key="cat2.id"
            >
              <div
                draggable="true"
                @dragstart="(e) => dragStart(e, cat2)"
                @dragend="dragEnd"
              >
                {{ cat2.title }}
              </div>
            </li>
          </ul>
        </li>
        <li v-if="drag">
          <div
            class="drop-target"
            @drop="(e) => dragDrop(e, null)"
            @dragover="(e) => dragOver(e)"
            @dragleave="(e) => dragLeave(e)"
          ></div>
        </li>
      </ul>
    </v-card>

    <h2>Sort CO2 Questions</h2>
    <v-alert type="info"
      >Sort the items in the order of which the questions will be
      presented.</v-alert
    >
    <SortItems :items="[...props.data]" @change="save" />
  </v-container>
</template>
<style scoped>
.draglist {
  list-style-type: square;
}

.item {
  display: flex;
  flex-direction: row;
}
.name {
  width: 300px;
  cursor: grab;
}
.drop-target {
  width: 200px;
  height: 24px;
  border: dashed 1px black;
  border-radius: 4px;
  border-color: rgb(0, 0, 0);
}
.drop-target.drag-active {
  border-color: rgba(46, 126, 246, 1);
  background-color: rgba(46, 126, 246, 0.5);
}
</style>
